import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { Upload, FileCode2, Trash2 } from 'lucide-react';

interface Template {
  id: string; name: string; scope: string; scopeId: string | null; version: number; active: boolean; uploadedAt: string;
}

export default function TemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function fetchTemplates() {
    try { setLoading(true); setTemplates(await api('/templates')); }
    catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  useEffect(() => { fetchTemplates(); }, []);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !name.trim()) return;
    setBusy(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api('/templates', {
        method: 'POST',
        body: JSON.stringify({ name, fileBase64: base64, scope: 'DEFAULT' }),
      });
      setFile(null); setName(''); fetchTemplates();
    } catch (err: any) { setError(err.message || 'Upload failed'); }
    finally { setBusy(false); }
  }

  async function handleDelete(id: string) {
    try { await api(`/templates/${id}`, { method: 'DELETE' }); fetchTemplates(); }
    catch {}
  }

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'scope', header: 'Scope' },
    { key: 'version', header: 'Version', render: (tm: Template) => <span className="font-mono text-xs">{tm.version}</span> },
    { key: 'active', header: 'Active', render: (tm: Template) => <Badge variant={tm.active ? 'success' : 'secondary'}>{tm.active ? 'Yes' : 'No'}</Badge> },
    { key: 'actions', header: '', className: 'w-10', render: (tm: Template) => (
      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(tm.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
    )},
  ];

  const mobileRender = (tm: Template) => (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><FileCode2 className="h-4 w-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{tm.name}</div>
        <div className="text-xs text-[var(--color-muted-foreground)]">{tm.scope} · v{tm.version}</div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={tm.active ? 'success' : 'secondary'}>{tm.active ? 'Yes' : 'No'}</Badge>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(tm.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  return (
    <div className="animate-in space-y-6">
      <PageHeader title={t('templates') || 'Templates'} />
      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Upload className="h-4 w-4 text-[var(--color-primary)]" /> Upload Template</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px] space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Template name" required />
            </div>
            <div className="flex-1 min-w-[200px] space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">File (.xlsx)</Label>
              <div className="flex h-10 items-center gap-2 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-muted)] px-3 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-accent)] transition-colors">
                <Upload className="h-4 w-4" />
                <input type="file" accept=".xlsx" required onChange={e => setFile(e.target.files?.[0] || null)} className="w-full bg-transparent text-sm outline-none" />
              </div>
            </div>
            <Button type="submit" loading={busy} disabled={busy}>{busy ? 'Uploading...' : 'Upload'}</Button>
          </form>
          {error && <Badge variant="destructive" className="mt-3">{error}</Badge>}
        </CardContent>
      </Card>

      {loading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <DataTable
          columns={columns}
          data={templates}
          keyExtractor={(tm) => tm.id}
          mobileRender={mobileRender}
          emptyState={<EmptyState icon={FileCode2} title="No templates" description="Upload your first template to get started." />}
        />
      )}
    </div>
  );
}
